import React, { useState, useRef, useEffect } from "react";
import { MdDeleteOutline } from "react-icons/md";
import { IoAddCircleOutline } from "react-icons/io5";
import UplaodIcon from "Assets/Images/upload.svg";
import { AddNewJob } from "Services/OtherApis";
import toast from "react-hot-toast";
import { useNavigate } from "react-router-dom";

import { useTranslation } from "react-i18next";

const AddJobRole = () => {
  const navigate = useNavigate();
  const { t, i18n } = useTranslation();
  const isRTL = i18n.dir() === "rtl";
  const fileInputRef = useRef(null);
  const [isLoading, setIsLoading] = useState(false);
  const [selectedLanguage, setSelectedLanguage] = useState(
    isRTL ? "Arabic" : "English"
  );

  const [optionState, setOptionState] = useState("");
  const [data, setData] = useState({
    jobTitle: {
      ar: "",
      en: "",
    },
    jobType: {
      ar: options?.find((item) => item.id === optionState)?.ar || "",
      en: options?.find((item) => item.id === optionState)?.en || "",
    },
    description: {
      ar: "",
      en: "",
    },
    experience: {
      ar: "",
      en: "",
    },
    location: {
      ar: "",
      en: "",
    },
    duties: {
      ar: [""],
      en: [""],
    },
    requiredDocuments: {
      ar: [""],
      en: [""],
    },
    qualifications: {
      ar: [""],
      en: [""],
    },
    image: "",
  });

  useEffect(() => {
    if (optionState) {
      const selectedOption = options?.find((item) => item.id === optionState);
      setData((prevData) => ({
        ...prevData,
        jobType: {
          ar: selectedOption?.ar || "",
          en: selectedOption?.en || "",
        },
      }));
    }
  }, [optionState, options]); // Ensure it runs when options change as well.

  const handleSubmit = (e) => {
    e.preventDefault();
    if (validateData()) {
      // Uncomment the following line to send data to the server
      postJob(data);
    }
  };
  function handleClick() {
    fileInputRef.current.click();
  }

  function handleSelectImage(e) {
    if (e.target.files && e.target.files[0]) {
      const selectedImage = e.target.files[0];

      if (selectedImage.size > 2 * 1024 * 1024) {
        // Check if the image size exceeds 2MB
        alert(t("Please upload an image smaller than 2MB."));
        e.target.value = null;
        return;
      }

      if (
        selectedImage.type === "image/png" ||
        selectedImage.type === "image/jpeg" || // Added support for JPEG
        selectedImage.type === "image/jpg" || // This is not typically used but kept for completeness
        selectedImage.type === "image/x-icon"
      ) {
        const reader = new FileReader();
        reader.onload = (event) => {
          const base64Image = event.target.result;
          setData({ ...data, image: base64Image });
        };

        reader.readAsDataURL(selectedImage);
      } else {
        alert("Please upload a PNG or ICO file.");
        e.target.value = null;
      }
    }
  }

  async function postJob(data) {
    const normalizedJobData = normalizeJobData(data);

    setIsLoading(true);
    try {
      const res = await AddNewJob(normalizedJobData);
      if (res?.status === 200) {
        toast.success("Job Added successfully!");
        navigate("/");
      } else {
        toast.error(res || "Error posting job!");
      }
    } catch (e) {
      console.error("Error posting job:", e);
    } finally {
      setIsLoading(false);
    }
  }

  function normalizeJobData(jobData) {
    const fields = [
      "jobTitle",
      "jobType",
      "description",
      "experience",
      "location",
      "duties",
      "requiredDocuments",
      "qualifications",
    ];

    // Check if all fields in English are fully filled
    let hasCompleteEnglishData = fields.every((key) =>
      Array.isArray(jobData[key].en)
        ? jobData[key].en.length > 0
        : jobData[key].en?.trim()
    );

    // Check if all fields in Arabic are fully filled
    let hasCompleteArabicData = fields.every((key) =>
      Array.isArray(jobData[key].ar)
        ? jobData[key].ar.length > 0
        : jobData[key].ar?.trim()
    );

    if (hasCompleteEnglishData && !hasCompleteArabicData) {
      // User provided complete English data but Arabic is incomplete → clear Arabic fields
      fields.forEach((key) => {
        jobData[key].ar = Array.isArray(jobData[key].en) ? [] : "";
      });
    } else if (hasCompleteArabicData && !hasCompleteEnglishData) {
      // User provided complete Arabic data but English is incomplete → clear English fields
      fields.forEach((key) => {
        jobData[key].en = Array.isArray(jobData[key].ar) ? [] : "";
      });
    }

    return jobData;
  }
  const validateData = () => {
    const isComplete = (lang) => {
      return (
        data.jobTitle[lang] &&
        data.jobType[lang] &&
        data.description[lang] &&
        data.experience[lang] &&
        data.location[lang] &&
        data.duties[lang].every((item) => item.trim() !== "") &&
        data.requiredDocuments[lang].every((item) => item.trim() !== "") &&
        data.qualifications[lang].every((item) => item.trim() !== "")
      );
    };
    if (!data.image) {
      toast.error("Please upload an image!");
      return false;
    }
    const enComplete = isComplete("en");
    const arComplete = isComplete("ar");

    if (!enComplete && !arComplete && !data.image) {
      toast.error("Please complete at least one language!");
      return false;
    }

    if (!enComplete || !arComplete) {
      const incompleteLang = enComplete ? "Arabic" : "English";
      return window.confirm(
        `You haven't completed the ${incompleteLang} fields. Do you want to proceed?`
      );
    }

    return true;
  };

  return (
    <div className="w-full mx-auto p-6 bg-white shadow-md rounded-lg">
      <div className="flex flex-row justify-between items-center mb-2">
        <h2 className=" mb-4 font-semibold text-xl  tracking-wide">
          {t("Add New Job Role")}
        </h2>
        <div>
          <select
            onChange={(e) => setSelectedLanguage(e.target.value)}
            required
            className={`select w-full mt-1 p-2 border border-gray-200 rounded-lg shadow-sm focus:ring-blue-500 focus:border-blue-500 ${
              isRTL ? "text-right pr-8 pl-5" : "pr-10"
            }`}
          >
            <option value="English">{t("English")}</option>
            <option value="Arabic">{t("Arabic")}</option>
          </select>
        </div>
      </div>

      <form onSubmit={handleSubmit} className="space-y-4">
        {selectedLanguage === "English" ? (
          <div>
            <div className="flex flex-row space-x-5 rtl:space-x-reverse">
              <div className="flex flex-col   w-1/2">
                <div>
                  <label className="block text-sm font-medium text-gray-700">
                    {t("Job Title")} <span className="text-red-500">*</span>
                  </label>
                  <input
                    required
                    type="text"
                    value={data.jobTitle.en}
                    onChange={(e) =>
                      setData({
                        ...data,
                        jobTitle: { ...data.jobTitle, en: e.target.value },
                      })
                    }
                    className="w-full mt-1 p-2 border border-gray-200 rounded-lg shadow-sm focus:ring-blue-500 focus:border-blue-500"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mt-4">
                    {t("Job Type")} <span className="text-red-500">*</span>
                  </label>
                  <select
                    required
                    name="jobType"
                    value={optionState}
                    onChange={
                      (e) => setOptionState(e.target.value)
                      // setData({
                      //   ...data,
                      //   jobType: { ...data.jobType, en: e.target.value },
                      // })
                    }
                    className={`w-full mt-1 p-2 border border-gray-200 rounded-lg shadow-sm focus:ring-blue-500 focus:border-blue-500 ${
                      isRTL ? "text-right pr-10 pl-5" : ""
                    }`}
                  >
                    <option value="">Select Job Type</option>
                    {options.map((v, k) => {
                      return <option value={v?.id}>{v?.en}</option>;
                    })}
                  </select>
                </div>

                <div className="mt-4">
                  <label className="block text-sm font-medium text-gray-700">
                    {t("Experience")}
                    <span className="px-1 text-red-500">*</span>
                  </label>
                  <input
                    type="text"
                    required
                    value={data.experience.en}
                    onChange={(e) =>
                      setData({
                        ...data,
                        experience: { ...data.experience, en: e.target.value },
                      })
                    }
                    className="w-full mt-1 p-2 border border-gray-200 rounded-lg shadow-sm focus:ring-blue-500 focus:border-blue-500"
                  />
                </div>
              </div>
              <div className="w-1/2">
                <div
                  onClick={handleClick}
                  className="w-full border h-60 cursor-pointer bg-secondry rounded-md border-dashed	 border-gray-200 items-center flex flex-col justify-center px-4 py-8"
                >
                  {!data?.image && <img src={UplaodIcon} />}

                  {data?.image ? (
                    <img src={data?.image} className="h-44" />
                  ) : (
                    <div className="flex flex-col items-center justify-center">
                      <a className="font-semibold mt-3">
                        <span className="underline text-indigo-800 font-bold cursor-pointer hover:opacity-80 duration-300">
                          {t("Browse")}
                          <span className="px-1 text-red-500">*</span>
                        </span>
                      </a>
                      <a className="text-xs text-gray-600 dark:text-dark0 mt-3">
                        {t("Supported formates: Ico, PNG")}
                      </a>
                    </div>
                  )}
                </div>
              </div>
            </div>
            <input
              ref={fileInputRef}
              type="file"
              onChange={handleSelectImage}
              style={{ display: "none" }}
            />
            <div className="mt-4">
              <label className="block text-sm font-medium text-gray-700">
                {t("Description")}
                <span className="px-1 text-red-500">*</span>
              </label>
              <textarea
                required
                value={data.description.en}
                onChange={(e) =>
                  setData({
                    ...data,
                    description: { ...data.description, en: e.target.value },
                  })
                }
                rows="4"
                className="w-full mt-1 p-2 min-h-[125px] border border-gray-200 rounded-lg shadow-sm focus:ring-blue-500 focus:border-blue-500"
              ></textarea>
            </div>
            <div className="pb-3 mt-4">
              <label className="block text-sm font-medium text-gray-700">
                {t("Location/Region")}
                <span className="px-1 text-red-500">*</span>
              </label>
              <input
                type="text"
                value={data.location.en}
                onChange={(e) =>
                  setData({
                    ...data,
                    location: { ...data.location, en: e.target.value },
                  })
                }
                required
                className="w-full mt-1 p-2 border border-gray-200 rounded-lg shadow-sm focus:ring-blue-500 focus:border-blue-500"
              />
            </div>
            <div className="border p-4 rounded-lg border-gray-200 mt-4">
              <div className="flex flex-row justify-between">
                <label className="block text-sm font-bold text-gray-700">
                  {t("Duties")}
                  <span className="px-1 text-red-500">*</span>
                </label>
                <IoAddCircleOutline
                  onClick={() => {
                    const updatedDuties = [...data.duties.en, ""];
                    setData({
                      ...data,
                      duties: {
                        ...data.duties,
                        en: updatedDuties,
                      },
                    });
                  }}
                  className="text-3xl text-green-500 cursor-pointer hover:text-green-700 duration-300"
                />
              </div>
              {data.duties.en.map((duty, index) => (
                <div
                  key={index}
                  className="flex items-center space-x-2 mt-2 rtl:space-x-reverse"
                >
                  <input
                    required
                    type="text"
                    value={duty}
                    onChange={(e) => {
                      const updatedDuties = [...data.duties.en];
                      updatedDuties[index] = e.target.value;
                      setData({
                        ...data,
                        duties: {
                          ...data.duties,
                          en: updatedDuties,
                        },
                      });
                    }}
                    className="flex-grow p-2 border border-gray-200 rounded-lg shadow-sm focus:ring-blue-500 focus:border-blue-500"
                  />

                  {data.duties.en.length > 1 && (
                    <MdDeleteOutline
                      onClick={() => {
                        if (data.duties.en.length > 1) {
                          const updatedDuties = [...data.duties.en];
                          updatedDuties.splice(index, 1);
                          setData({
                            ...data,
                            duties: {
                              ...data.duties,
                              en: updatedDuties,
                            },
                          });
                        }
                      }}
                      className="text-3xl text-red-500 cursor-pointer hover:text-red-700 duration-300"
                    />
                  )}
                </div>
              ))}
            </div>

            <div className="border p-4 rounded-lg border-gray-200 mt-4">
              <div className="flex flex-row justify-between">
                <label className="block text-sm font-bold text-gray-700">
                  {t("Required Documents")}
                  <span className="px-1 text-red-500">*</span>
                </label>
                <IoAddCircleOutline
                  onClick={() => {
                    const updatedDocuments = [...data.requiredDocuments.en, ""];
                    setData({
                      ...data,
                      requiredDocuments: {
                        ...data.requiredDocuments,
                        en: updatedDocuments,
                      },
                    });
                  }}
                  className="text-3xl text-green-500 cursor-pointer hover:text-green-700 duration-300"
                />
              </div>
              {data.requiredDocuments.en.map((doc, index) => (
                <div
                  key={index}
                  className="flex items-center space-x-2 mt-2 rtl:space-x-reverse"
                >
                  <input
                    required
                    type="text"
                    value={doc}
                    onChange={(e) => {
                      const updatedDocuments = [...data.requiredDocuments.en];
                      updatedDocuments[index] = e.target.value;
                      setData({
                        ...data,
                        requiredDocuments: {
                          ...data.requiredDocuments,
                          en: updatedDocuments,
                        },
                      });
                    }}
                    className="flex-grow p-2 border border-gray-200 rounded-lg shadow-sm focus:ring-blue-500 focus:border-blue-500"
                  />

                  {data.requiredDocuments.en.length > 1 && (
                    <MdDeleteOutline
                      onClick={() => {
                        if (data.requiredDocuments.en.length > 1) {
                          const updatedDocuments = [
                            ...data.requiredDocuments.en,
                          ];
                          updatedDocuments.splice(index, 1);
                          setData({
                            ...data,
                            requiredDocuments: {
                              ...data.requiredDocuments,
                              en: updatedDocuments,
                            },
                          });
                        }
                      }}
                      className="text-3xl text-red-500 cursor-pointer hover:text-red-700 duration-300"
                    />
                  )}
                </div>
              ))}
            </div>

            <div className="border p-4 rounded-lg border-gray-200 mt-4">
              <div className="flex flex-row justify-between">
                <label className="block text-sm font-bold text-gray-700">
                  {t("Qualifications")}
                  <span className="px-1 text-red-500">*</span>
                </label>
                <IoAddCircleOutline
                  onClick={() => {
                    const updatedQualifications = [
                      ...data.qualifications.en,
                      "",
                    ];
                    setData({
                      ...data,
                      qualifications: {
                        ...data.qualifications,
                        en: updatedQualifications,
                      },
                    });
                  }}
                  className="text-3xl text-green-500 cursor-pointer hover:text-green-700 duration-300"
                />
              </div>
              {data.qualifications.en.map((qualification, index) => (
                <div
                  key={index}
                  className="flex items-center space-x-2 rtl:space-x-reverse mt-2"
                >
                  <input
                    type="text"
                    required
                    value={qualification}
                    onChange={(e) => {
                      const updatedQualifications = [...data.qualifications.en];
                      updatedQualifications[index] = e.target.value;
                      setData({
                        ...data,
                        qualifications: {
                          ...data.qualifications,
                          en: updatedQualifications,
                        },
                      });
                    }}
                    className="flex-grow p-2 border border-gray-200 rounded-lg shadow-sm focus:ring-blue-500 focus:border-blue-500"
                  />

                  {data.qualifications.en.length > 1 && (
                    <MdDeleteOutline
                      onClick={() => {
                        if (data.qualifications.en.length > 1) {
                          const updatedQualifications = [
                            ...data.qualifications.en,
                          ];
                          updatedQualifications.splice(index, 1);
                          setData({
                            ...data,
                            qualifications: {
                              ...data.qualifications,
                              en: updatedQualifications,
                            },
                          });
                        }
                      }}
                      className="text-3xl text-red-500 cursor-pointer hover:text-red-700 duration-300"
                    />
                  )}
                </div>
              ))}
            </div>
          </div>
        ) : (
          <div dir="rtl">
            <div className="flex flex-row space-x-5 rtl:space-x-reverse">
              <div className="flex flex-col   w-1/2">
                <div>
                  <label className="block text-sm font-medium text-gray-700">
                    {t("Job Title")}
                    <span className="px-1 text-red-500">*</span>
                  </label>
                  <input
                    required
                    type="text"
                    value={data.jobTitle.ar}
                    onChange={(e) =>
                      setData({
                        ...data,
                        jobTitle: { ...data.jobTitle, ar: e.target.value },
                      })
                    }
                    className="w-full mt-1 p-2 border border-gray-200 rounded-lg shadow-sm focus:ring-blue-500 focus:border-blue-500"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mt-4">
                    {t("Job Type")}
                    <span className="px-1 text-red-500">*</span>
                  </label>
                  <select
                    required
                    name="jobType"
                    value={optionState}
                    onChange={
                      (e) => setOptionState(e.target.value)
                      // setData({
                      //   ...data,
                      //   jobType: { ...data.jobType, ar: e.target.value },
                      // }),
                    }
                    className={`select w-full mt-1 p-2 border border-gray-200 rounded-lg shadow-sm focus:ring-blue-500 focus:border-blue-500 ${
                      isRTL ? "text-right pr-10 pl-5" : "pr-10"
                    }  `}
                  >
                    <option value="">اختر نوع الوظيفة</option>
                    {options.map((v, k) => {
                      return <option value={v?.id}>{v?.ar}</option>;
                    })}
                  </select>
                </div>

                <div className="mt-4">
                  <label className="block text-sm font-medium text-gray-700">
                    {t("Experience")}
                    <span className="px-1 text-red-500">*</span>
                  </label>
                  <input
                    required
                    type="text"
                    value={data.experience.ar}
                    onChange={(e) =>
                      setData({
                        ...data,
                        experience: { ...data.experience, ar: e.target.value },
                      })
                    }
                    className="w-full mt-1 p-2 border border-gray-200 rounded-lg shadow-sm focus:ring-blue-500 focus:border-blue-500"
                  />
                </div>
              </div>
              <div className="w-1/2">
                <div
                  required
                  onClick={handleClick}
                  className="w-full border h-60 cursor-pointer bg-secondry rounded-md border-dashed	 border-gray-200 items-center flex flex-col justify-center px-4 py-8"
                >
                  {!data?.image && <img src={UplaodIcon} />}

                  {data?.image ? (
                    <img src={data?.image} className="h-44" />
                  ) : (
                    <div className="flex flex-col items-center justify-center">
                      <a className="font-semibold mt-3">
                        <span className="underline text-indigo-800 font-bold cursor-pointer hover:opacity-80 duration-300">
                          {t("Browse")}
                          <span className="px-1 text-red-500">*</span>
                        </span>
                      </a>
                      <a className="text-xs text-gray-600 dark:text-dark0 mt-3">
                        {t("Supported formates: Ico, PNG")}
                      </a>
                    </div>
                  )}
                </div>
              </div>
            </div>
            <input
              ref={fileInputRef}
              type="file"
              onChange={handleSelectImage}
              style={{ display: "none" }}
            />
            <div className="mt-4">
              <label className="block text-sm font-medium text-gray-700">
                {t("Description")}
                <span className="px-1 text-red-500">*</span>
              </label>
              <textarea
                required
                value={data.description.ar}
                onChange={(e) =>
                  setData({
                    ...data,
                    description: { ...data.description, ar: e.target.value },
                  })
                }
                rows="4"
                className="w-full mt-1 p-2 min-h-[125px] border border-gray-200 rounded-lg shadow-sm focus:ring-blue-500 focus:border-blue-500"
              ></textarea>
            </div>
            <div className="pb-3 mt-4">
              <label className="block text-sm font-medium text-gray-700">
                {t("Location/Region")}
                <span className="px-1 text-red-500">*</span>
              </label>
              <input
                type="text"
                value={data.location.ar}
                onChange={(e) =>
                  setData({
                    ...data,
                    location: { ...data.location, ar: e.target.value },
                  })
                }
                required
                className="w-full mt-1 p-2 border border-gray-200 rounded-lg shadow-sm focus:ring-blue-500 focus:border-blue-500"
              />
            </div>
            <div className="border p-4 rounded-lg border-gray-200 mt-4">
              <div className="flex flex-row justify-between">
                <label className="block text-sm font-bold text-gray-700">
                  {t("Duties")}
                  <span className="px-1 text-red-500">*</span>
                </label>
                <IoAddCircleOutline
                  onClick={() => {
                    const updatedDuties = [...data.duties.ar, ""];
                    setData({
                      ...data,
                      duties: {
                        ...data.duties,
                        ar: updatedDuties,
                      },
                    });
                  }}
                  className="text-3xl text-green-500 cursor-pointer hover:text-green-700 duration-300"
                />
              </div>
              {data.duties.ar.map((duty, index) => (
                <div
                  key={index}
                  className="flex items-center space-x-2 mt-2 rtl:space-x-reverse"
                >
                  <input
                    type="text"
                    value={duty}
                    onChange={(e) => {
                      const updatedDuties = [...data.duties.ar];
                      updatedDuties[index] = e.target.value;
                      setData({
                        ...data,
                        duties: {
                          ...data.duties,
                          ar: updatedDuties,
                        },
                      });
                    }}
                    required
                    className="flex-grow p-2 border border-gray-200 rounded-lg shadow-sm focus:ring-blue-500 focus:border-blue-500"
                  />

                  {data.duties.ar.length > 1 && (
                    <MdDeleteOutline
                      onClick={() => {
                        if (data.duties.ar.length > 1) {
                          const updatedDuties = [...data.duties.ar];
                          updatedDuties.splice(index, 1);
                          setData({
                            ...data,
                            duties: {
                              ...data.duties,
                              ar: updatedDuties,
                            },
                          });
                        }
                      }}
                      className="text-3xl text-red-500 cursor-pointer hover:text-red-700 duration-300"
                    />
                  )}
                </div>
              ))}
            </div>

            <div className="border p-4 rounded-lg border-gray-200 mt-4">
              <div className="flex flex-row justify-between">
                <label className="block text-sm font-bold text-gray-700">
                  {t("Required Documents")}
                  <span className="px-1 text-red-500">*</span>
                </label>
                <IoAddCircleOutline
                  onClick={() => {
                    const updatedDocuments = [...data.requiredDocuments.ar, ""];
                    setData({
                      ...data,
                      requiredDocuments: {
                        ...data.requiredDocuments,
                        ar: updatedDocuments,
                      },
                    });
                  }}
                  className="text-3xl text-green-500 cursor-pointer hover:text-green-700 duration-300"
                />
              </div>
              {data.requiredDocuments.ar.map((doc, index) => (
                <div
                  key={index}
                  className="flex items-center space-x-2 mt-2 rtl:space-x-reverse"
                >
                  <input
                    type="text"
                    value={doc}
                    required
                    onChange={(e) => {
                      const updatedDocuments = [...data.requiredDocuments.ar];
                      updatedDocuments[index] = e.target.value;
                      setData({
                        ...data,
                        requiredDocuments: {
                          ...data.requiredDocuments,
                          ar: updatedDocuments,
                        },
                      });
                    }}
                    className="flex-grow p-2 border border-gray-200 rounded-lg shadow-sm focus:ring-blue-500 focus:border-blue-500"
                  />

                  {data.requiredDocuments.ar.length > 1 && (
                    <MdDeleteOutline
                      onClick={() => {
                        if (data.requiredDocuments.ar.length > 1) {
                          const updatedDocuments = [
                            ...data.requiredDocuments.ar,
                          ];
                          updatedDocuments.splice(index, 1);
                          setData({
                            ...data,
                            requiredDocuments: {
                              ...data.requiredDocuments,
                              ar: updatedDocuments,
                            },
                          });
                        }
                      }}
                      className="text-3xl text-red-500 cursor-pointer hover:text-red-700 duration-300"
                    />
                  )}
                </div>
              ))}
            </div>

            <div className="border p-4 rounded-lg border-gray-200  mt-4">
              <div className="flex flex-row justify-between">
                <label className="block text-sm font-bold text-gray-700">
                  {t("Qualifications")}
                  <span className="px-1 text-red-500">*</span>
                </label>

                <IoAddCircleOutline
                  onClick={() => {
                    const updatedQualifications = [
                      ...data.qualifications.ar,
                      "",
                    ];
                    setData({
                      ...data,
                      qualifications: {
                        ...data.qualifications,
                        ar: updatedQualifications,
                      },
                    });
                  }}
                  className="text-3xl text-green-500 cursor-pointer hover:text-green-700 duration-300"
                />
              </div>

              {data?.qualifications?.ar?.map((qualification, index) => (
                <div
                  key={index}
                  className="flex items-center space-x-2 mt-2 rtl:space-x-reverse"
                >
                  <input
                    type="text"
                    value={qualification}
                    onChange={(e) => {
                      const updatedQualifications = [...data.qualifications.ar];
                      updatedQualifications[index] = e.target.value;
                      setData({
                        ...data,
                        qualifications: {
                          ...data.qualifications,
                          ar: updatedQualifications,
                        },
                      });
                    }}
                    required
                    className="flex-grow p-2 border border-gray-200 rounded-lg shadow-sm focus:ring-blue-500 focus:border-blue-500"
                  />

                  {data.qualifications.ar.length > 1 && (
                    <MdDeleteOutline
                      onClick={() => {
                        if (data.qualifications.ar.length > 1) {
                          const updatedQualifications = [
                            ...data.qualifications.ar,
                          ];
                          updatedQualifications.splice(index, 1);
                          setData({
                            ...data,
                            qualifications: {
                              ...data.qualifications,
                              ar: updatedQualifications,
                            },
                          });
                        }
                      }}
                      className="text-3xl text-red-500 cursor-pointer hover:text-red-700 duration-300"
                    />
                  )}
                </div>
              ))}
            </div>
          </div>
        )}

        <div className="flex items-center">
          <button
            type="submit"
            className={`mt-4 px-4 py-2 rounded-md ml-auto flex items-center justify-center ${
              isLoading ? "bg-gray-500 cursor-not-allowed" : "bg-indigo-800"
            } text-white`}
            disabled={isLoading} // Disable button when loading
          >
            {isLoading ? (
              <>
                <svg
                  className="animate-spin h-5 w-5 mr-2 text-white"
                  xmlns="http://www.w3.org/2000/svg"
                  fill="none"
                  viewBox="0 0 24 24"
                >
                  <circle
                    className="opacity-25"
                    cx="12"
                    cy="12"
                    r="10"
                    stroke="currentColor"
                    strokeWidth="4"
                  ></circle>
                  <path
                    className="opacity-75"
                    fill="currentColor"
                    d="M4 12a8 8 0 018-8v4l3-3-3-3v4a8 8 0 00-8 8h4l-3 3 3 3v-4z"
                  ></path>
                </svg>
                {t("Loading...")}
              </>
            ) : (
              t("Submit")
            )}
          </button>
        </div>
      </form>
    </div>
  );
};

export default AddJobRole;

const options = [
  { id: "1", en: "Full Time", ar: "دوام كامل" },
  { id: "2", en: "Part Time", ar: "دوام جزئي" },
  { id: "3", en: "Internship", ar: "تدريب" },
  { id: "4", en: "Contract", ar: "عقد" },
];
